import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { RoutineItemInfo } from "./routineItemSlice";
import { routineItemRemove } from "./routineItemInsUpdSlice";

export interface RoutineItemListState {
    state: LoadStateInfo;
    request?: RetrieveRoutineItemListRequest;
    list?: RoutineItemInfo[];
}

const initialState: RoutineItemListState = {
    state: notStarted(),
};


export interface RetrieveRoutineItemListRequest {
    routineId: string;
}
export const retrieveRoutineItemList = createAsyncThunk(
    "retrieveRoutineItemList",
    async (param: RetrieveRoutineItemListRequest, thunkAPI) => {
        console.debug(`retrieve routine items for ${param.routineId}`);

        const response = await fetch(`${getBackendUrl()}/api/routine/${param.routineId}/items`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveRoutineItemListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.routineItemList;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveRoutineItemList skipped: ${status.state.state}`);
            return false;
        }
    }
);


export const routineItemListSlice = createSlice({
    name: "routineItemList",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        check: (state, action: PayloadAction<RetrieveRoutineItemListRequest>) => {
            if (state.state.state != LoadState.NotStarted
                && (state.request && state.request.routineId === action.payload.routineId)
            ) {
                console.trace(`routine item list already retrieved for routine ${action.payload.routineId}`);
            } else {
                console.debug(`routine item list not retrieved for routine ${action.payload.routineId}`);
                state.state = notStarted();
                state.request = action.payload;
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveRoutineItemList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveRoutineItemList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            })
            .addCase(retrieveRoutineItemList.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(routineItemRemove.fulfilled, (state, action) => {
                console.debug(`List :: ${action.type}`);
                if(state.list && action.meta.arg) {
                    state.list = state.list.filter( (item) => item.id !== action.meta.arg?.routineItem.id );
                }
            })
            ;
    },
});

// export const { } = routineItemListSlice.actions;

export const selectRoutineItemList = (state: RootState) => state.routineItemList;

export default routineItemListSlice.reducer;
